import { Box, Button, Stack, Typography } from '@mui/material';
import { LockOpenIcon } from 'lucide-react';

import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useTeamTransactionsSharing } from '@modules/reports/cma-reports/step-transaction/hooks/useTeamTransactionsSharing';
import { useLocale } from '@shared/hooks/locale';

const OptOutTransaction = () => {
  const { t } = useLocale();
  const { me } = useAppData();
  const currentUserTeamId = me?.default_team?.id;

  // Check if current user is team leader for their default team
  const isTeamLeader =
    me?.teams?.some(
      teamUser => teamUser.team_id === currentUserTeamId && teamUser.is_leader,
    ) ?? false;

  const { optIn, loading: updatingTeam } =
    useTeamTransactionsSharing(currentUserTeamId);

  const handleOptIn = async () => {
    if (!currentUserTeamId) {
      console.error('No team ID available for opt-in');
      return;
    }

    try {
      await optIn();
    } catch (error) {
      console.error('Failed to opt in:', error);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
          }}
        >
          <LockOpenIcon size={18} style={{ marginRight: 8 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary',
            }}
          >
            {t('Unlock real transaction data')}
          </Typography>
        </Stack>

        <Typography variant="body1">
          {t(
            'Contribute to a shared, anonymised database of real estate transactions and gain access to valuable market insights.',
          )}
        </Typography>

        <Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            {t('You can view:')}
          </Typography>
          <Typography variant="body1" sx={{ pl: 2, mb: 0.5 }}>
            • {t('Approximate location of the sold property (postcode)')}
          </Typography>
          <Typography variant="body1" sx={{ pl: 2, mb: 0.5 }}>
            • {t('Number of rooms')}
          </Typography>
          <Typography variant="body1" sx={{ pl: 2, mb: 0.5 }}>
            • {t('Surface')}
          </Typography>
          <Typography variant="body1" sx={{ pl: 2 }}>
            • {t('Transaction price')}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            {t('Your data stays protected:')}
          </Typography>
          <Typography variant="body1" sx={{ pl: 2, mb: 0.5 }}>
            • {t('No exact address is shared')}
          </Typography>
          <Typography variant="body1" sx={{ pl: 2 }}>
            • {t('No seller or agent details disclosed')}
          </Typography>
        </Box>

        <Typography variant="body1">
          {t(
            'Join now to contribute your transactions to the data pool and enable more comprehensive and accurate price analyses – for you and your peers.',
          )}
        </Typography>

        <Box>
          <Button
            variant="contained"
            onClick={handleOptIn}
            loading={updatingTeam}
            loadingPosition="start"
            disabled={!isTeamLeader}
          >
            {t('Join to access transaction data')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default OptOutTransaction;
